import memoize from 'memoize-one';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { createEngagementReporter } from 'my-core/analytics/engagement-report';
import {
  getOcActivityProperties,
  getOnlineCourseEventProperties,
  onlineCourseActivityCompleted,
  onlineCourseActivityDurationViewed,
  onlineCourseActivityTokenPurchased,
  onlineCourseActivityViewed,
  trackCustomEvent,
} from 'my-core/gtm-events';
import { useStandardApiRequest } from 'my-core/hooks';
import { ACTIVITY_TYPES_CONFIG } from 'my-core/online-course-activity-utils';
import { findAncestor, getNext, getPrev } from 'my-core/online-course-content-map';

import { fetchOnlineCourseActivityApi } from 'my-actions/OnlineCourseActions';

const TICK_INTERVAL = 1000 * 60;

export function useActiveComponent({
  activeComponent,
  assessmentId,
  basePath,
  canViewActivity,
  course,
  disabled,
  inactive,
  onlineCourse,
  onSaveProgress,
  onTokenPurchase,
  progress,
  tokenPurchaseStatus,
}) {
  const chapter = findAncestor(activeComponent, a => a.type === 'chapter');
  const section = findAncestor(activeComponent, a => a.type === 'section');
  const ocActivity = activeComponent?.data;

  const privateContent = !canViewActivity && !!ocActivity?.activity_metadata?.is_private;

  const nextAndPrev = useMemo(() => {
    const next = activeComponent && getNext(activeComponent, n => n.type === 'activity');
    const prev = activeComponent && getPrev(activeComponent, n => n.type === 'activity');
    return {
      next: next && { path: `${basePath}${next.fullPath}`, node: next },
      prev: prev && { path: `${basePath}${prev.fullPath}`, node: prev },
    };
  }, [activeComponent, basePath]);
  const navigate = useNavigate();
  const goToNext = useMemo(
    () => !!nextAndPrev.next?.path && (() => navigate(nextAndPrev.next.path)),
    [navigate, nextAndPrev.next?.path],
  );

  const {
    activity,
    fetching: fetchingActivity,
    fetchStatus: fetchActivityStatus,
  } = useFetchActivity(
    onlineCourse,
    !privateContent && activeComponent?.type === 'activity' && ocActivity,
    canViewActivity,
  );

  const eventTrackingProperties = useMemo(
    () => ({
      ...getOnlineCourseEventProperties({ ...onlineCourse, course }),
      ...getOcActivityProperties(ocActivity),
      instructor_id: activity?.user?.id,
      instructor_name: activity?.user?.name,
      chapter_id: chapter?.data?.id,
      section_id: section?.data?.id,
      assessment_id: assessmentId,
    }),
    [
      course,
      onlineCourse,
      ocActivity,
      activity?.user?.id,
      activity?.user?.name,
      chapter?.data?.id,
      section?.data?.id,
      assessmentId,
    ],
  );

  // Token Purchase
  const tokenPurchase = useCallback(() => onTokenPurchase(ocActivity.id), [ocActivity?.id, onTokenPurchase]);
  useEffect(() => {
    if (tokenPurchaseStatus?.success) {
      onlineCourseActivityTokenPurchased(eventTrackingProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPurchaseStatus]);

  // Subscription Trial
  // const startTrial = useCallback(() => onStartTrial(eventTrackingProperties), [eventTrackingProperties, onStartTrial]);

  const saveActivityProgress = useCallback(
    (...args) => onSaveProgress(ocActivity.id, ...args),
    [ocActivity?.id, onSaveProgress],
  );

  const timestamp = useRef();
  const recordElapsedRef = useRef();
  const viewedDurationRef = useRef({ seconds: 0 });
  useEffect(() => {
    if (fetchActivityStatus?.success && !disabled && !inactive) {
      timestamp.current = Date.now();
      let rep = 0;
      let timeout = null;
      const record = () => {
        const seconds = Math.round((Date.now() - timestamp.current) / 1000);
        timestamp.current = Date.now();
        if (seconds) {
          saveActivityProgress({ update_duration: seconds });
          viewedDurationRef.current = {
            seconds: viewedDurationRef.current.seconds + seconds,
            canView: canViewActivity,
            properties: eventTrackingProperties,
          };
        }
      };
      const tick = function () {
        rep++;
        clearTimeout(timeout);
        timeout = setTimeout(tick, TICK_INTERVAL * rep);
        record();
      };
      recordElapsedRef.current = record;
      timeout = setTimeout(tick, TICK_INTERVAL);
      return () => {
        record();
        clearTimeout(timeout);
        timestamp.current = undefined;
        recordElapsedRef.current = undefined;
      };
    }
  }, [canViewActivity, disabled, eventTrackingProperties, fetchActivityStatus, saveActivityProgress, inactive]);

  // After the timer effect, so leaving an activity reports the time its cleanup just recorded.
  useEffect(() => {
    if (!ocActivity?.id) return;
    const reporter = createEngagementReporter({
      collect: () => {
        recordElapsedRef.current?.();
        const viewed = viewedDurationRef.current;
        viewedDurationRef.current = { seconds: 0 };
        return viewed.seconds && viewed;
      },
      send: ({ canView, properties, seconds }, options) =>
        onlineCourseActivityDurationViewed(seconds, canView, properties, options),
    });
    return reporter.stop;
  }, [ocActivity?.id]);

  useEffect(() => {
    if (fetchActivityStatus?.success) {
      saveActivityProgress({ is_current: true, assessment_id: assessmentId });
      onlineCourseActivityViewed(canViewActivity, eventTrackingProperties);
    }
  }, [assessmentId, canViewActivity, eventTrackingProperties, fetchActivityStatus, saveActivityProgress]);

  const handleActivityCompleted = useCallback(() => {
    saveActivityProgress({ is_completed: true }, { immediate: true });
    onlineCourseActivityCompleted(
      (progress?.duration || 0) + (timestamp.current ? Math.round((Date.now() - timestamp.current) / 1000) : 0),
      canViewActivity,
      eventTrackingProperties,
    );
  }, [progress?.duration, canViewActivity, eventTrackingProperties, saveActivityProgress]);

  return {
    activity,
    chapter,
    fetchActivityStatus,
    eventTrackingProperties,
    fetchingActivity,
    goToNext,
    handleActivityCompleted: progress?.is_completed ? undefined : handleActivityCompleted,
    nextAndPrev,
    privateContent,
    saveActivityProgress,
    section,
    tokenPurchase,
  };
}

/*








 */

function useFetchActivity(onlineCourse, ocActivity, canView) {
  const activityFetchKey = `online_course_activity_${ocActivity?.id}_${canView ? 'full' : 'preview'}`;

  const { performRequest: performFetchActivity, requestStatus } = useStandardApiRequest({
    actionCreator: fetchOnlineCourseActivityApi,
    requestKey: activityFetchKey,
    onError: e => e?.status === 429 && trackCustomEvent('Online Course Activity Rate Limited'),
  });

  useEffect(() => {
    if (
      ocActivity &&
      (!requestStatus ||
        (!requestStatus.fetching &&
          requestStatus.error?.status === 429 &&
          Date.now() > requestStatus.fetched_at + 20_000))
    ) {
      performFetchActivity(
        onlineCourse.id,
        ocActivity.id,
        activityFetchKey,
        ACTIVITY_TYPES_CONFIG[ocActivity.activity_type].fetchParams[canView ? 'full' : 'preview'],
      );
    }
  }, [activityFetchKey, canView, requestStatus, ocActivity, onlineCourse.id, performFetchActivity]);

  const activity = useSelector(state =>
    getActivity(
      ocActivity?.activity_type,
      ocActivity?.activity_id,
      state.courseLessons.items,
      state.quizQuestions.items,
      state.quizzes.items,
      state.users.items,
    ),
  );

  return {
    activity,
    fetching: !!ocActivity && (!requestStatus || requestStatus.fetching),
    fetchStatus: requestStatus,
  };
}

const getActivity = memoize((activityType, activityId, courseLessons, quizQuestions, quizzes, users) => {
  if (activityType && activityId) {
    const state = { courseLessons, quizQuestions, quizzes };
    const act = state[ACTIVITY_TYPES_CONFIG[activityType]?.storeKey]?.[activityId];
    return act && { ...act, user: users[act.user_id] };
  }
  return null;
});
